// Where voice input listens: the microphone, a browser tab (a call or a
// video) or the whole screen's audio. Shared by the dictation hook and the
// source menu.

export const VOICE_SOURCES = { MIC: 'mic', TAB: 'tab', SCREEN: 'screen' }

export const isCallSource = source =>
    source === VOICE_SOURCES.TAB || source === VOICE_SOURCES.SCREEN

export const isDisplayAudioSupported = () =>
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia

// Only Chromium browsers deliver audio from getDisplayMedia; Firefox and
// Safari resolve the call but hand back video only.
export const isChromiumBrowser = () =>
    typeof window !== 'undefined' &&
    (!!window.chrome || /Edg\//.test(navigator.userAgent || ''))

export const platformName = () => {
    if (typeof navigator === 'undefined') return 'other'
    const platform =
        navigator.userAgentData?.platform || navigator.platform || ''
    if (/win/i.test(platform)) return 'windows'
    if (/mac/i.test(platform)) return 'mac'
    if (/linux|cros|android/i.test(platform)) return 'linux'
    return 'other'
}

export const isCallCaptureAvailable = () =>
    isDisplayAudioSupported() && isChromiumBrowser()

export const platformHint = source => {
    if (!isCallCaptureAvailable()) {
        return 'Tab and screen audio need Chrome or Edge.'
    }
    if (source === VOICE_SOURCES.SCREEN) {
        switch (platformName()) {
            case 'windows':
                return 'Pick "Entire screen" and tick "Share system audio".'
            case 'mac':
                return 'Needs macOS 14.2+ and Chrome 141+: pick "Entire screen" and tick "Share system audio".'
            default:
                return "System audio isn't available here; share the call's tab instead."
        }
    }
    return 'In the share dialog pick the tab of the call or video and tick "Also share tab audio".'
}

export const noAudioSharedMessage = source =>
    source === VOICE_SOURCES.SCREEN
        ? 'No audio was shared. Choose "Entire screen" again and tick "Share system audio".'
        : 'No audio was shared. Choose the tab again and tick "Also share tab audio".'

const SOURCE_KEY = 'voiceInputSource'
const INCLUDE_MIC_KEY = 'voiceInputIncludeMic'

export const readVoiceSourcePreference = () => {
    try {
        const source = localStorage.getItem(SOURCE_KEY)
        const includeMic = localStorage.getItem(INCLUDE_MIC_KEY)
        return {
            source: Object.values(VOICE_SOURCES).includes(source)
                ? source
                : VOICE_SOURCES.MIC,
            includeMic: includeMic === null ? true : includeMic === '1',
        }
    } catch {
        return { source: VOICE_SOURCES.MIC, includeMic: true }
    }
}

export const saveVoiceSourcePreference = ({ source, includeMic }) => {
    try {
        localStorage.setItem(SOURCE_KEY, source)
        localStorage.setItem(INCLUDE_MIC_KEY, includeMic ? '1' : '0')
    } catch {}
}
